import React, { useEffect, useState } from 'react';
import './VendorManagement.css';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000/api';

const getAuthHeaders = (headers = {}) => {
  const token = localStorage.getItem('authToken');
  return token ? { ...headers, Authorization: `Bearer ${token}` } : headers;
};

const getVendorName = (vendor) => vendor?.name || '';

const EngineVendorManagement = () => {
  const [vendors, setVendors] = useState([]);
  const [filteredVendors, setFilteredVendors] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [formData, setFormData] = useState({ name: '' });

  useEffect(() => {
    fetchVendors();
  }, []);

  useEffect(() => {
    const normalizedSearch = searchTerm.toLowerCase();
    const filtered = vendors.filter((vendor) =>
      getVendorName(vendor).toLowerCase().includes(normalizedSearch)
    );
    setFilteredVendors(filtered);
  }, [vendors, searchTerm]);

  const fetchVendors = async () => {
    setLoading(true);
    setError(null);

    try {
      const response = await fetch(`${API_BASE_URL}/engine-vendors`, {
        headers: getAuthHeaders(),
      });

      if (!response.ok) {
        throw new Error('Erreur lors du chargement');
      }

      const data = await response.json();
      setVendors(data.data || data || []);
    } catch (err) {
      setError(err.message);
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleEdit = (vendor) => {
    setFormData({ name: getVendorName(vendor) });
    setEditingId(vendor.id);
    setShowForm(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    try {
      const method = editingId ? 'PUT' : 'POST';
      const url = editingId
        ? `${API_BASE_URL}/engine-vendors/${editingId}`
        : `${API_BASE_URL}/engine-vendors`;

      const response = await fetch(url, {
        method,
        headers: getAuthHeaders({ 'Content-Type': 'application/json' }),
        body: JSON.stringify(formData),
      });

      const result = await response.json();

      if (!response.ok) {
        throw new Error(result.error || result.message || 'Erreur lors de la sauvegarde');
      }

      setFormData({ name: '' });
      setEditingId(null);
      setShowForm(false);
      await fetchVendors();
    } catch (err) {
      setError(err.message);
      console.error('Erreur:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Etes-vous sur de vouloir supprimer ce vendeur moteur ?')) {
      return;
    }

    setLoading(true);

    try {
      const response = await fetch(`${API_BASE_URL}/engine-vendors/${id}`, {
        method: 'DELETE',
        headers: getAuthHeaders(),
      });

      const result = await response.json();

      if (!response.ok) {
        throw new Error(result.error || result.message || 'Erreur lors de la suppression');
      }

      await fetchVendors();
    } catch (err) {
      setError(err.message);
      console.error('Erreur:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    setShowForm(false);
    setEditingId(null);
    setFormData({ name: '' });
  };

  return (
    <div className="vendor-management">
      <h2>Gestion des Vendeurs Moteur</h2>

      {error && <div className="error-message">{error}</div>}

      {!showForm ? (
        <>
          <div className="search-bar">
            <input
              type="text"
              placeholder="Rechercher un vendeur moteur..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
            <button
              className="btn btn-primary"
              onClick={() => setShowForm(true)}
              disabled={loading}
            >
              + Ajouter Vendeur
            </button>
          </div>

          {loading ? (
            <div className="loading">Chargement...</div>
          ) : (
            <div className="vendors-list">
              {filteredVendors.length === 0 ? (
                <p className="no-data">Aucun vendeur moteur trouve</p>
              ) : (
                <table className="vendors-table">
                  <thead>
                    <tr>
                      <th>Nom</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredVendors.map((vendor, index) => (
                      <tr key={vendor.id || `${getVendorName(vendor)}-${index}`}>
                        <td>{getVendorName(vendor) || '-'}</td>
                        <td>
                          <button
                            className="btn btn-edit"
                            onClick={() => handleEdit(vendor)}
                            disabled={loading}
                          >
                            Modifier
                          </button>
                          <button
                            className="btn btn-delete"
                            onClick={() => handleDelete(vendor.id)}
                            disabled={loading}
                          >
                            Supprimer
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          )}
        </>
      ) : (
        <div className="form-container">
          <h3>{editingId ? 'Modifier Vendeur Moteur' : 'Ajouter Vendeur Moteur'}</h3>
          <form onSubmit={handleSubmit}>
            <div className="form-group">
              <label>Nom *</label>
              <input
                type="text"
                name="name"
                value={formData.name}
                onChange={handleInputChange}
                required
                placeholder="Nom du vendeur moteur"
              />
            </div>

            <div className="form-actions">
              <button type="submit" className="btn btn-success" disabled={loading}>
                {loading ? 'Enregistrement...' : 'Enregistrer'}
              </button>
              <button
                type="button"
                className="btn btn-secondary"
                onClick={handleCancel}
                disabled={loading}
              >
                Annuler
              </button>
            </div>
          </form>
        </div>
      )}
    </div>
  );
};

export default EngineVendorManagement;
